import React, { useState, useEffect, ReactNode } from 'react';
import { useRouter } from 'next/router';
import { Box, Container, Typography, Button, Paper } from '@mui/material';
import { useAuth } from 'contexts/AuthContext';
import { useIdleTimerHook } from 'hooks/useIdleTimer';
import { IdleTimeoutDialog } from 'components/LMA/IdleTimeoutDialog/IdleTimeoutDialog';
import LockIcon from '@mui/icons-material/Lock';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Text, RichText, TextField } from '@sitecore-jss/sitecore-jss-nextjs';

interface AccessDeniedFields {
  AccessDeniedTitle?: TextField;
  AccessDeniedSummary?: TextField;
  AccessDeniedInstructions?: { value: string };
  AccessDeniedCTAButton?: {
    value: {
      href?: string;
      text?: string;
    };
  };
  AccessDeniedHelpText?: TextField;
}

interface AutoLogoutFields {
  AutoLogoutTimeoutSeconds?: { value: number };
  AutoLogoutPromptTimeoutSeconds?: { value: number };
  AutoLogoutTitle?: TextField;
  AutoLogoutMessage?: { value: string }; // Rich Text (HTML)
  AutoLogoutCountdownLabel?: TextField;
  AutoLogoutContinueText?: TextField;
  AutoLogoutContinueButtonText?: TextField;
  AutoLogoutButton?: {
    value: {
      href?: string;
      text?: string;
    };
  };
}

interface AuthorizationProps {
  children: ReactNode;
  accessDeniedFields?: AccessDeniedFields;
  autoLogoutFields?: AutoLogoutFields;
}

export function Authorization({
  children,
  accessDeniedFields,
  autoLogoutFields,
}: AuthorizationProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState<number | undefined>(undefined);

  // Compute path-related variables at top level (needed for hooks)
  // Pages that require authentication
  const protectedPaths = [
    '/custom-app-v1',
    '/',
    '/ViewEnrollmentDetails/Referral',
    '/ViewEnrollmentDetails/Processed',
  ];
  // Pages that should be accessible without authentication
  const publicPaths = ['/test-auth', '/logout'];
  // Use asPath for actual URL (handles Sitecore rewrites), remove query string
  const currentPath = router.asPath.split('?')[0];
  const isPublicPath = publicPaths.some((path) => currentPath.startsWith(path));
  const requiresAuth = protectedPaths.some((path) =>
    path === '/' ? currentPath === '/' : currentPath.startsWith(path)
  );

  // Get timeout values from Sitecore fields or use defaults (1200s and 600s)
  const idleTimeoutMs = (autoLogoutFields?.AutoLogoutTimeoutSeconds?.value ?? 1200) * 1000;
  const idlePromptTimeoutMs =
    (autoLogoutFields?.AutoLogoutPromptTimeoutSeconds?.value ?? 600) * 1000;

  // Get logout redirect URL from Sitecore or default to /logout
  const logoutRedirectUrl = autoLogoutFields?.AutoLogoutButton?.value?.href || '/logout';

  // Idle timer - only active when authenticated, mounted, and on protected pages
  const shouldEnableIdleTimer = mounted && isAuthenticated && !isPublicPath && requiresAuth;

  useEffect(() => {
    setMounted(true);
  }, []);

  // Debug: Log the timeout values from Sitecore
  useEffect(() => {
    if (shouldEnableIdleTimer) {
      console.log('Idle timer enabled:', {
        idleTimeoutSeconds: idleTimeoutMs / 1000,
        promptTimeoutSeconds: idlePromptTimeoutMs / 1000,
        logoutRedirectUrl: logoutRedirectUrl,
      });
    }
  }, [shouldEnableIdleTimer, idleTimeoutMs, idlePromptTimeoutMs, logoutRedirectUrl]);

  const {
    isPrompted,
    continueSession,
    logout: handleIdleLogout,
  } = useIdleTimerHook({
    onIdle: () => {
      // Set a flag to indicate this is an idle timeout logout
      // This ensures the logout page is accessible even after token is cleared
      sessionStorage.setItem('lma-idle-logout', 'true');
      // Redirect to logout page (from Sitecore or default)
      window.location.href = logoutRedirectUrl;
      // Note: logout() is called by LogoutPage component on mount
    },
    onPrompt: () => {
      // onPrompt callback - countdown will be handled by useEffect
    },
    idleTime: idleTimeoutMs,
    promptTime: idlePromptTimeoutMs,
    enabled: shouldEnableIdleTimer,
  });

  // Countdown timer for the dialog
  useEffect(() => {
    if (!isPrompted) {
      setRemainingSeconds(undefined);
      return;
    }

    const promptTime = idlePromptTimeoutMs;
    const startTime = Date.now();

    const countdownInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, Math.floor((promptTime - elapsed) / 1000));
      setRemainingSeconds(remaining);

      if (remaining <= 0) {
        clearInterval(countdownInterval);
        setRemainingSeconds(undefined);
      }
    }, 1000);

    return () => {
      clearInterval(countdownInterval);
    };
  }, [isPrompted, idlePromptTimeoutMs]);

  const handleContinue = () => {
    continueSession();
    setRemainingSeconds(undefined);
  };

  const handleLogout = () => {
    handleIdleLogout();
    setRemainingSeconds(undefined);
  };

  // --- RENDER LOGIC (all hooks are called above this point) ---

  // Loading component for reuse
  const LoadingScreen = (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
      }}
    >
      <Typography variant="h6" color="text.secondary">
        Loading...
      </Typography>
    </Box>
  );

  // ALWAYS show loading until client-side mounted
  // This prevents SSR flash since router.asPath may not be accurate during SSR
  if (!mounted) {
    return LoadingScreen;
  }

  // After mounting, we can trust router.asPath for path detection
  // If the current page is public or doesn't require auth, render children without checking
  if (isPublicPath || !requiresAuth) {
    return <>{children}</>;
  }

  // For protected routes: show loading while auth state is being determined
  if (isLoading) {
    return LoadingScreen;
  }

  // If not authenticated, show unauthorized page
  if (!isAuthenticated) {
    return <UnauthorizedPage fields={accessDeniedFields} />;
  }

  // If authenticated, render children with idle timeout dialog
  return (
    <>
      {children}
      <IdleTimeoutDialog
        open={isPrompted}
        onContinue={handleContinue}
        onLogout={handleLogout}
        remainingTime={remainingSeconds}
        fields={autoLogoutFields}
      />
    </>
  );
}

interface UnauthorizedPageProps {
  fields?: AccessDeniedFields;
}

function UnauthorizedPage({ fields }: UnauthorizedPageProps) {
  const handleGoBack = () => {
    // Use Sitecore CTA button href if available
    const href = fields?.AccessDeniedCTAButton?.value?.href;
    if (href) {
      window.location.href = href;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #255E5E 0%, #1a4a4a 100%)',
        padding: 3,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M50 20 L50 40 M30 40 L70 40 M30 40 L30 60 L70 60 L70 40 M45 40 L45 50 M55 40 L55 50' stroke='rgba(255,255,255,0.15)' stroke-width='2' fill='none'/%3E%3C/svg%3E"),
            url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M40 15 L40 35 M25 35 L55 35 M25 35 L25 55 L55 55 L55 35 M35 35 L35 45 M45 35 L45 45' stroke='rgba(255,255,255,0.1)' stroke-width='2' fill='none'/%3E%3C/svg%3E"),
            repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255, 255, 255, 0.03) 2px, rgba(255, 255, 255, 0.03) 4px),
            repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255, 255, 255, 0.03) 2px, rgba(255, 255, 255, 0.03) 4px)
          `,
          backgroundSize: '100px 100px, 80px 80px, 20px 20px, 20px 20px',
          backgroundPosition: '0 0, 50px 50px, 0 0, 0 0',
          animation: 'lockPatternMove 25s linear infinite',
          pointerEvents: 'none',
          zIndex: 0,
          opacity: 0.6,
        },
        '@keyframes lockPatternMove': {
          '0%': {
            transform: 'translate(0, 0)',
          },
          '100%': {
            transform: 'translate(100px, 100px)',
          },
        },
      }}
    >
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, py: 3 }}>
        <Paper
          elevation={24}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            textAlign: 'center',
            borderRadius: 4,
            backgroundColor: 'white',
            position: 'relative',
            zIndex: 1,
            maxHeight: 'calc(100vh - 48px)',
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Lock Icon - Top */}
            <Box
              sx={{
                width: { xs: 100, sm: 120 },
                height: { xs: 100, sm: 120 },
                borderRadius: '50%',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '4px solid #e0e0e0',
                mb: 3,
              }}
            >
              <LockIcon
                sx={{
                  fontSize: { xs: 50, sm: 60 },
                  color: '#255E5E',
                }}
              />
            </Box>

            {/* Title - from Sitecore */}
            {fields?.AccessDeniedTitle?.value && (
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: '#212121',
                  mb: 1,
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  fontSize: {
                    xs: '1.5rem',
                    sm: '2rem',
                    md: '2.25rem',
                  },
                  lineHeight: 1.2,
                  textAlign: 'center',
                }}
              >
                <Text field={fields.AccessDeniedTitle} />
              </Typography>
            )}

            {/* Summary - from Sitecore */}
            {fields?.AccessDeniedSummary?.value && (
              <Typography
                variant="body1"
                component="p"
                sx={{
                  color: '#616161',
                  mb: 3,
                  lineHeight: 1.6,
                  textAlign: 'center',
                }}
              >
                <Text field={fields.AccessDeniedSummary} />
              </Typography>
            )}

            {/* Instructions and Actions */}
            <Box
              sx={{
                width: '100%',
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              {/* Instructions - from Sitecore as RichText */}
              {fields?.AccessDeniedInstructions?.value && (
                <Box
                  sx={{
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2,
                    p: 2.5,
                    mb: 3,
                    textAlign: 'left',
                    '& h2, & h3, & h4': {
                      color: '#424242',
                      mb: 2,
                      fontWeight: 600,
                      fontSize: '1rem',
                      margin: 0,
                      marginBottom: '16px',
                    },
                    '& ol, & ul': {
                      pl: 3,
                      color: '#616161',
                      margin: 0,
                      '& li': {
                        mb: 1.5,
                        lineHeight: 1.8,
                      },
                    },
                  }}
                >
                  <RichText field={fields.AccessDeniedInstructions} />
                </Box>
              )}

              {/* Action Button and Help Text */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  alignItems: 'center',
                  mt: 3,
                }}
              >
                {/* CTA Button - from Sitecore */}
                {fields?.AccessDeniedCTAButton?.value?.href && (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleGoBack}
                    sx={{
                      px: 4,
                      py: 1,
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: 2,
                      backgroundColor: '#255E5E',
                      '&:hover': {
                        backgroundColor: '#1a4a4a',
                      },
                    }}
                  >
                    {fields.AccessDeniedCTAButton.value.text}
                  </Button>
                )}

                {/* Help Text - from Sitecore */}
                {fields?.AccessDeniedHelpText?.value && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#9e9e9e',
                      fontStyle: 'italic',
                      textAlign: 'center',
                    }}
                  >
                    <Text field={fields.AccessDeniedHelpText} />
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
